import Box from '@mui/material/Box';
import {SxProps, Theme} from '@mui/material/styles';
import {kit, radius} from './tokens';

export interface LifecycleStep {
  label: string;
}

interface LifecycleTrackerProps {
  steps: LifecycleStep[];
  // Index of the current (in-progress) step. Steps before it are done,
  // steps after it are upcoming.
  currentIndex: number;
  sx?: SxProps<Theme>;
}

type StepStatus = 'done' | 'now' | 'upcoming';

const CheckIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FFFFFF"
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// 4-step (or N-step) horizontal lifecycle tracker -- the web port of the
// approved `.track` / `.tk` markup in web-detail-1-quotes.html. Done steps
// get a solid blue check, the current step gets a ringed dot, upcoming steps
// stay a plain gray outline; the connecting line ahead of a done/current step
// fills blue.
const LifecycleTracker = ({steps, currentIndex, sx}: LifecycleTrackerProps) => (
  <Box
    sx={[
      {display: 'flex', alignItems: 'flex-start', padding: '20px 6px 2px'},
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}>
    {steps.map((step, index) => {
      const status: StepStatus =
        index < currentIndex
          ? 'done'
          : index === currentIndex
            ? 'now'
            : 'upcoming';

      return (
        <Box
          key={step.label}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            position: 'relative',
          }}>
          {index > 0 ? (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: '-50%',
                width: '100%',
                height: '2px',
                backgroundColor: status === 'upcoming' ? kit.line : kit.blue,
                zIndex: 1,
              }}
            />
          ) : null}
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: `${radius.pill}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              ...(status === 'done' && {
                backgroundColor: kit.blue,
                border: `2px solid ${kit.blue}`,
              }),
              ...(status === 'now' && {
                backgroundColor: kit.blueTint,
                border: `2px solid ${kit.blue}`,
              }),
              ...(status === 'upcoming' && {
                backgroundColor: '#FFFFFF',
                border: `2px solid ${kit.line}`,
              }),
            }}>
            {status === 'done' ? <CheckIcon /> : null}
            {status === 'now' ? (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: `${radius.pill}px`,
                  backgroundColor: kit.blue,
                }}
              />
            ) : null}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: 11.5,
              fontWeight: status === 'now' ? 700 : 600,
              color:
                status === 'done'
                  ? kit.inkSoft
                  : status === 'now'
                    ? kit.blueInk
                    : kit.slate2,
            }}>
            {step.label}
          </Box>
        </Box>
      );
    })}
  </Box>
);

export default LifecycleTracker;
